import React, { useContext, useEffect, useCallback, useMemo } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { convertViewThemeToCSSProperties } from '@utils/theme'
import { ThemeContext } from '@base/ThemeContext'
import { useHorizontalTimelineContext } from '../context'
import { initialize, changeSize } from '../actions'
import { minWidthRatioSelector } from '../selectors/scroller_selectors'
import ScrubberDisplayContainer from '../containers/ScrubberDisplayContainer'
import TimelineDisplayContainer from '../containers/TimelineDisplayContainer'
import type { TimelineDeriveFn, TimelineDerivationContext } from '../derivation/strategies'
import Provider from '../provider'

type HorizontalTimelineCoreProps = {
  derivationContext: TimelineDerivationContext
  deriveTimelineItems: TimelineDeriveFn
  photos?: Record<string | number, any>
}

const HorizontalTimelineCore: React.FC<HorizontalTimelineCoreProps> = props => {
  const { derivationContext, deriveTimelineItems, photos = {} } = props
  const { state, dispatch } = useHorizontalTimelineContext()
  const theme = useContext(ThemeContext)
  useEffect(() => {
    if (!state.initialized && state.size.width > 0 && state.size.height > 0) {
      const minWidthRatio = minWidthRatioSelector(state, {})
      dispatch(initialize(minWidthRatio))
    }
  }, [state.initialized, state.size.width, state.size.height])

  const handleResize = useCallback((w?: number, h?: number) => {
    if (w && w > 0 && h && h > 0) dispatch(changeSize(w, h))
  }, [])

  const { ref } = useResizeDetector({ onResize: handleResize })

  // derive items
  const { markers, timespans } = useMemo(
    () => deriveTimelineItems(derivationContext),
    [derivationContext.rawData, derivationContext.fields, derivationContext.viewOptions, deriveTimelineItems]
  )

  const readyToRender = state.initialized && state.size.width > 0 && state.size.height > 0
  let content: React.ReactNode = null
  if (readyToRender) {
    const themeStyle: React.CSSProperties = convertViewThemeToCSSProperties(theme)
    content = (
      <div className="timeline timeline--horizontal" style={themeStyle}>
        <div className="timeline__content">
          <ScrubberDisplayContainer markers={markers} timespans={timespans} />
          <TimelineDisplayContainer timeline={{} as any} markers={markers} timespans={timespans} photos={photos} />
        </div>
      </div>
    )
  }

  return (
    <div id="timeline-fullscreenable" className="full-size-scrollable resizable-content" ref={ref}>
      {content}
    </div>
  )
}

export const ProvidedHorizontalTimelineCore: React.FC<HorizontalTimelineCoreProps> = props => (
  <Provider>
    <HorizontalTimelineCore {...props} />
  </Provider>
)

export default ProvidedHorizontalTimelineCore
